'use client';

/**
 * Landing-Page für athlyn.de (vormals coachai-vercel.vercel.app)
 *
 * Senior-Webdesigner-Pass (2026-05-19): vom 4-Sektionen-Skelett zur
 * vollständigen Marketing-Story. Motion-Grafiken (floating phone,
 * count-up stats, scroll-fades, shimmer), Feature-Deep-Dives im
 * Zigzag-Layout mit App-Mockups, Privacy-/Trust-Block, FAQ.
 *
 * Eigenständig vom ThemeProvider gebaut — das Landing soll sich vom
 * In-App-Feel absetzen (dark-premium statt 3-Mode-Theme).
 *
 * iOS-Capacitor erkennt sich selbst und redirected zu /app/ — die
 * Marketing-Seite ergibt in der nativen App keinen Sinn.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ConsentBanner, useScrollDepth, trackEvent } from '../shared/marketing';
import {
  IconCamera,
  IconZap,
  IconTarget,
  IconCheck,
  IconDroplet,
  IconLotus,
  IconDumbbell,
  IconScale,
  IconMeal,
  IconCalendar,
  IconClock,
  IconInfo,
  IconX,
} from '../components/Icons';
import { AthlynLogo } from '../shared/ui';

// ── Brand Constants ──────────────────────────────────────
// Static export läuft ohne `trailingSlash` (sonst Deadlock im Export-Build),
// daher liegt die App-Seite als `app.html` vor — der Capacitor-Redirect muss
// das explizite File-Ziel ansteuern, nicht `/app/` (würde im Native-Container
// 404en). Greift nur im Native-Build (w.Capacitor), Web/Vercel unberührt.
const APP_URL = '/app.html';

const BRAND = {
  // Dark anchors — Slate-Charcoal als Brand-Foundation
  bg: '#0A0D12',
  bgAlt: '#11151C',
  bgGlass: 'rgba(10, 13, 18, 0.78)',

  // Light anchors — warmes Cream statt steriles Weiß
  surface: '#FFFFFF',
  surfaceTint: '#F5F2EB',
  surfaceTint2: '#EDE9DE',

  // Text
  text: '#0A0D12',
  textSoft: '#2F3640',
  textOnDark: '#FFFFFF',
  muted: '#5A5F66',
  mutedOnDark: '#A6ACB5',

  // Brand-Akzent: gedämpftes Athletic-Lime (entneont ggü. #B5E832)
  accent: '#9BC22E',               // ruhigeres Lime — weniger Neon, premium-athletic
  accentDeep: '#7A9D22',           // gradient pair
  accentLight: '#C7DD8A',          // soft headlines / shimmer
  accentSoft: 'rgba(155, 194, 46, 0.09)',
  accentSoftDark: 'rgba(155, 194, 46, 0.16)',
  accentText: '#0A0D12',           // dark text on lime — critical for readability

  // Borders
  border: '#E0DCD0',
  borderDark: '#1F252F',

  // Semantic (state colors only)
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#60A5FA',
} as const;

// Hero-Hintergrund: zwei Welten, die ineinanderfließen.
// TEMPORÄR Unsplash-Stock — vor Launch durch lizenzierte/eigene Fotos ersetzen.
// Links: Küche / Essenszubereitung. Rechts: Freihantelbereich im Gym.
// Puck-Breite ist ~204px (Retina ~408px) — w=2000 war massiv überdimensioniert
// und kostete LCP/Bandbreite. w=600&q=60 reicht für die kleine, abgedunkelte
// Kachel und lädt deutlich schneller.
const HERO_IMG_KITCHEN =
  'https://images.unsplash.com/photo-1577219492769-b63a779fac28?w=600&q=60&auto=format&fit=crop';
const HERO_IMG_GYM =
  'https://images.unsplash.com/photo-1576678927484-cc907957088c?w=600&q=60&auto=format&fit=crop';

// ─── Capacitor Redirect Hook ─────────────────────────────
function useCapacitorRedirect() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as unknown as { Capacitor?: unknown };
    if (w.Capacitor) {
      window.location.replace(APP_URL);
    }
  }, []);
}

// ─── Scroll-In Fade Hook ─────────────────────────────────
function useFadeIn<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, visible };
}

// ─── Count-Up Hook ───────────────────────────────────────
function useCountUp(target: number, durationMs = 1200, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return value;
}

// ═════════════════════════════════════════════════════════
// PAGE
// ═════════════════════════════════════════════════════════

export default function LandingPage() {
  useCapacitorRedirect();
  useScrollDepth();

  return (
    <>
      <GlobalStyles />
      <ConsentBanner />
      <main
        style={{
          background: BRAND.surface,
          color: BRAND.text,
          minHeight: '100vh',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          overflowX: 'hidden',
        }}
      >
        <TopNav />
        <Hero />
        <StatsBar />
        <Comparison />
        <HowItWorks />
        <FeatureFotoTracking />
        <FeatureCoachChat />
        <FeatureTraining />
        <FeatureGamification />
        <FeatureInsights />
        <BiasBlock />
        <PrivacyTrust />
        <FaqSection />
        <FinalCTA />
        <Footer />
      </main>
    </>
  );
}

// ═════════════════════════════════════════════════════════
// GLOBAL STYLES (keyframes + responsive)
// ═════════════════════════════════════════════════════════

function GlobalStyles() {
  return (
    <style>{`
      /* Landing-Page-Reset: globals.css setzt am <body> ein
         max-width: 480px (mobile App-Shell für /app/). Auf der
         Marketing-Landing muss der Body volle Viewport-Breite haben,
         sonst werden die maxWidth:1120-Sektionen + .grid-2 auf ~480px
         geklemmt → Desktop zeigt fälschlich eine schmale 1-Spalten-Säule
         mit großen Rändern. Dieser Override gilt nur auf der Landing,
         da GlobalStyles ausschließlich in app/page.tsx gerendert wird. */
      body {
        max-width: none !important;
        margin: 0 !important;
      }

      @keyframes floatY {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-12px); }
      }
      @keyframes pulseGlow {
        0%, 100% { box-shadow: 0 0 0 0 ${BRAND.accent}40; }
        50% { box-shadow: 0 0 0 16px ${BRAND.accent}00; }
      }
      @keyframes ringDraw {
        from { stroke-dashoffset: 264; }
        to { stroke-dashoffset: 80; }
      }
      @keyframes fillBar {
        from { width: 0%; }
      }
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes typeDot {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
        30% { transform: translateY(-4px); opacity: 1; }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(24px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes gradientShift {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }

      .fade-in {
        opacity: 0;
        transform: translateY(24px);
        transition: opacity 700ms cubic-bezier(0.16, 1, 0.3, 1),
                    transform 700ms cubic-bezier(0.16, 1, 0.3, 1);
      }
      .fade-in.visible {
        opacity: 1;
        transform: translateY(0);
      }

      .shimmer-text {
        background: linear-gradient(
          90deg,
          ${BRAND.text} 0%,
          ${BRAND.text} 40%,
          ${BRAND.accent} 50%,
          ${BRAND.text} 60%,
          ${BRAND.text} 100%
        );
        background-size: 200% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 4s linear infinite;
      }

      .phone-float {
        animation: floatY 6s ease-in-out infinite;
        will-change: transform;
        backface-visibility: hidden;
        transform-style: preserve-3d;
      }

      .cta-pulse {
        animation: pulseGlow 2.4s ease-in-out infinite;
      }

      .type-dots span {
        display: inline-block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${BRAND.mutedOnDark};
        margin-right: 4px;
        animation: typeDot 1.2s ease-in-out infinite;
      }
      .type-dots span:nth-child(2) { animation-delay: 0.15s; }
      .type-dots span:nth-child(3) { animation-delay: 0.3s; }

      .gradient-bg {
        background: linear-gradient(
          135deg,
          ${BRAND.bg} 0%,
          ${BRAND.bgAlt} 50%,
          ${BRAND.bg} 100%
        );
        background-size: 200% 200%;
        animation: gradientShift 18s ease infinite;
      }

      /* Responsive — Mobile-first */
      .grid-2 {
        display: grid;
        grid-template-columns: 1fr;
        gap: 32px;
        align-items: center;
      }
      @media (min-width: 768px) {
        .grid-2 { grid-template-columns: 1fr 1fr; gap: 64px; }
      }

      .zigzag-reverse {
        grid-template-areas: "viz" "txt";
      }
      .zigzag-reverse .zz-text { grid-area: txt; }
      .zigzag-reverse .zz-viz  { grid-area: viz; }
      @media (min-width: 768px) {
        .zigzag-reverse {
          grid-template-areas: "viz txt";
          grid-template-columns: 1fr 1fr;
        }
      }

      .feature-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px;
      }
      @media (min-width: 720px) {
        .feature-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (min-width: 1000px) {
        .feature-grid { grid-template-columns: repeat(3, 1fr); }
      }

      .stats-row {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
      }
      @media (min-width: 720px) {
        .stats-row { grid-template-columns: repeat(4, 1fr); }
      }

      /* Comparison Section: Desktop = 3 cols (Topic | Them | Us),
         Mobile = stacked. Topic über die volle Breite, Them/Us
         darunter als 2 Spalten oder einzeln gestackt. */
      .cmp-headers { display: none !important; }
      .cmp-row {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
      .cmp-row .cmp-topic {
        font-size: 12px !important;
        font-weight: 700 !important;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: ${BRAND.accent} !important;
        margin-bottom: 4px;
      }
      @media (min-width: 720px) {
        .cmp-headers { display: grid !important; }
        .cmp-row {
          grid-template-columns: 160px 1fr 1fr !important;
          gap: 16px !important;
        }
        .cmp-row .cmp-topic {
          font-size: 14px !important;
          font-weight: 700 !important;
          text-transform: none;
          letter-spacing: 0;
          color: ${BRAND.textOnDark} !important;
          margin-bottom: 0;
        }
      }

      .nav-link { transition: color 200ms ease; }
      .nav-link:hover { color: ${BRAND.accent}; }

      .cta-primary {
        transition: transform 150ms ease, box-shadow 200ms ease;
      }
      .cta-primary:active { transform: scale(0.97); }
      .cta-primary:hover { box-shadow: 0 12px 32px ${BRAND.accent}55; }

      .feature-card {
        transition: transform 250ms ease, box-shadow 250ms ease, border-color 250ms ease;
      }
      .feature-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(0,0,0,0.06);
        border-color: ${BRAND.accent}40 !important;
      }

      /* Sticky nav backdrop blur */
      .nav-blur {
        backdrop-filter: saturate(180%) blur(20px);
        -webkit-backdrop-filter: saturate(180%) blur(20px);
      }

      /* ── Hero-Magnet: Ernährung + Fitness ziehen sich an → Athlyn entsteht ── */
      .magnet-stage {
        position: relative;
        width: 340px;
        height: 600px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .magnet-puck {
        position: absolute;
        left: 50%; top: 50%;
        width: 204px; height: 262px;
        margin: -131px 0 0 -102px;
        border-radius: 32px;
        background-size: cover;
        background-position: center;
        box-shadow: 0 30px 72px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.10);
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding-bottom: 18px;
        box-sizing: border-box;
        z-index: 2;
        will-change: transform, opacity;
      }
      .magnet-label {
        font-size: 14px;
        font-weight: 800;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #fff;
        text-shadow: 0 2px 12px rgba(0,0,0,0.75);
      }
      /* Eine weiche Beschleunigungskurve (kein Zittern, keine Rotation) = magnetisches Heranziehen */
      .magnet-puck--nutrition { animation: magnetLeft 3.4s cubic-bezier(.4,0,.2,1) forwards; }
      .magnet-puck--fitness   { animation: magnetRight 3.4s cubic-bezier(.4,0,.2,1) forwards; }
      @keyframes magnetLeft {
        0%   { transform: translateX(-200px) scale(.95); opacity: 0; }
        18%  { transform: translateX(-200px) scale(.95); opacity: 1; }
        68%  { transform: translateX(0) scale(1); opacity: 1; }
        82%  { transform: translateX(0) scale(.72); opacity: .6; }
        100% { transform: translateX(0) scale(.4); opacity: 0; }
      }
      @keyframes magnetRight {
        0%   { transform: translateX(200px) scale(.95); opacity: 0; }
        18%  { transform: translateX(200px) scale(.95); opacity: 1; }
        68%  { transform: translateX(0) scale(1); opacity: 1; }
        82%  { transform: translateX(0) scale(.72); opacity: .6; }
        100% { transform: translateX(0) scale(.4); opacity: 0; }
      }
      /* Anziehungsfeld — Lime-Streak zwischen den Polen, zieht sich vor Kontakt zusammen */
      .magnet-field {
        position: absolute;
        left: 50%; top: 50%;
        width: 380px; height: 100px;
        margin: -50px 0 0 -190px;
        border-radius: 50%;
        background: radial-gradient(ellipse at center, ${BRAND.accent}66 0%, transparent 70%);
        opacity: 0;
        z-index: 1;
        animation: magnetField 3.4s ease-in forwards;
        pointer-events: none;
      }
      @keyframes magnetField {
        0%, 20% { opacity: 0; transform: scaleX(1.5); }
        60%     { opacity: .7; transform: scaleX(.5); }
        70%     { opacity: 0; transform: scaleX(.22); }
        100%    { opacity: 0; }
      }
      /* Kollisions-Burst beim Aufprall */
      .magnet-burst {
        position: absolute;
        left: 50%; top: 50%;
        width: 300px; height: 300px;
        margin: -150px 0 0 -150px;
        border-radius: 50%;
        background: radial-gradient(circle, ${BRAND.accent}f0 0%, ${BRAND.accent}3a 34%, transparent 68%);
        opacity: 0;
        z-index: 3;
        animation: magnetBurst 3.4s ease-out forwards;
        pointer-events: none;
      }
      @keyframes magnetBurst {
        0%, 64% { opacity: 0; transform: scale(.25); }
        72%     { opacity: 1; transform: scale(1); }
        88%     { opacity: 0; transform: scale(1.7); }
        100%    { opacity: 0; }
      }
      /* Athlyn (Phone) entsteht aus dem Aufprall */
      .magnet-phone {
        position: relative;
        z-index: 4;
        opacity: 0;
        animation: magnetPhone 3.4s cubic-bezier(.2,.8,.25,1) forwards;
      }
      @keyframes magnetPhone {
        0%, 72% { opacity: 0; transform: scale(.55) translateY(18px); }
        86%     { opacity: 1; }
        100%    { opacity: 1; transform: scale(1) translateY(0); }
      }
      /* Phone-Screens: zuerst Startseite, dann Switch ins Dashboard (nach dem Emerge) */
      .phone-screens { position: absolute; inset: 0; }
      .phone-screen { position: absolute; inset: 0; }
      .phone-screen--start {
        z-index: 2;
        animation: screenOut 0.7s ease forwards;
        animation-delay: 5.4s;
      }
      .phone-screen--home {
        z-index: 1;
        opacity: 0;
        animation: screenIn 0.7s ease forwards;
        animation-delay: 5.4s;
      }
      @keyframes screenOut {
        0%   { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(1.05); }
      }
      @keyframes screenIn {
        0%   { opacity: 0; transform: scale(1.03); }
        100% { opacity: 1; transform: scale(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .magnet-puck, .magnet-field, .magnet-burst { display: none; }
        .magnet-phone { opacity: 1; animation: none; }
        .phone-screen--start { display: none; }
        .phone-screen--home { opacity: 1; animation: none; }
      }
      @media (max-width: 768px) {
        .magnet-stage { width: 300px; }
      }
    `}</style>
  );
}

// ═════════════════════════════════════════════════════════
// SECTIONS
// ═════════════════════════════════════════════════════════

// ─── TOP NAV ─────────────────────────────────────────────

function TopNav() {
  return (
    <nav
      className="nav-blur"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: BRAND.bgGlass,
        borderBottom: `1px solid ${BRAND.borderDark}`,
        padding: '14px 24px',
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <Brand white />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <a
            href={APP_URL}
            className="cta-primary"
            onClick={() => trackEvent('app_open', { location: 'nav' })}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              background: BRAND.accent,
              color: BRAND.accentText,
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Web öffnen
          </a>
        </div>
      </div>
    </nav>
  );
}

// ─── ATHLYN LOGO ─────────────────────────────────────────
function Brand({ white = false }: { white?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <AthlynLogo size={32} color={white ? BRAND.textOnDark : BRAND.text} />
      <span
        style={{
          fontSize: 17,
          fontWeight: 800,
          letterSpacing: -0.3,
          color: white ? BRAND.textOnDark : BRAND.text,
        }}
      >
        Athl<span style={{ color: BRAND.accent }}>yn</span>
      </span>
    </div>
  );
}

// ─── HERO ────────────────────────────────────────────────

function Hero() {
  return (
    <section
      className="gradient-bg"
      style={{
        color: BRAND.textOnDark,
        padding: '64px 24px 96px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Accent glow blobs */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -140,
          right: -120,
          width: 480,
          height: 480,
          borderRadius: '50%',
          background: BRAND.accent,
          opacity: 0.12,
          filter: 'blur(80px)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -200,
          left: -200,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: BRAND.accent,
          opacity: 0.04,
          filter: 'blur(120px)',
        }}
      />

      <div
        className="grid-2"
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* LEFT: copy */}
        <div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              background: BRAND.accentSoftDark,
              color: BRAND.accent,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginBottom: 28,
              textTransform: 'uppercase',
              border: `1px solid ${BRAND.accent}30`,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: BRAND.accent,
                boxShadow: `0 0 8px ${BRAND.accent}`,
              }}
            />
            Training + Ernährung. Eine App.
          </div>

          <h1
            style={{
              fontSize: 'clamp(40px, 7.5vw, 64px)',
              fontWeight: 800,
              lineHeight: 1.02,
              letterSpacing: -2,
              margin: 0,
              maxWidth: 560,
            }}
          >
            Disziplin schlägt<br />
            <span
              style={{
                background: `linear-gradient(135deg, ${BRAND.accent}, ${BRAND.accentLight})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Perfektion.
            </span>
          </h1>

          {/* Hero-Story: erst der emotionale Haken (Problem — woran die meisten
              scheitern), dann der USP (beide Welten in einem System, Foto statt
              Eintippen). */}
          <p
            style={{
              fontSize: 'clamp(16px, 2.2vw, 19px)',
              lineHeight: 1.55,
              color: BRAND.mutedOnDark,
              marginTop: 24,
              maxWidth: 520,
            }}
          >
            Die meisten scheitern nicht am Training, sondern an der Ernährung —
            weil exaktes Tracken zu mühsam war, um es durchzuhalten.
          </p>
          <p
            style={{
              fontSize: 'clamp(15px, 2vw, 17px)',
              lineHeight: 1.55,
              color: BRAND.textOnDark,
              marginTop: 14,
              maxWidth: 520,
              fontWeight: 500,
            }}
          >
            Athlyn dreht das um: Mahlzeit fotografieren statt eintippen, Training
            und Nährwerte zusammen, ein KI-Coach, der beides kennt. Nicht der
            perfekte Tag bringt dich ans Ziel, sondern der, den du{' '}
            <strong>wirklich durchziehst.</strong>
          </p>

          {/* CTAs */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 36,
            }}
          >
            <AppStoreBadge location="hero" />
            <a
              href={APP_URL}
              onClick={() => trackEvent('app_open', { location: 'hero' })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '15px 22px',
                borderRadius: 12,
                background: 'transparent',
                border: `1.5px solid ${BRAND.borderDark}`,
                color: BRAND.mutedOnDark,
                fontWeight: 600,
                fontSize: 15,
                textDecoration: 'none',
              }}
            >
              Im Browser testen →
            </a>
          </div>

          {/* Quick trust line */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginTop: 28,
              flexWrap: 'wrap',
            }}
          >
            <TrustChip icon={<IconCheck size={12} color={BRAND.accent} />} text="Kostenlos nutzbar" />
            <TrustChip icon={<IconCheck size={12} color={BRAND.accent} />} text="Kein Werbe-Tracking" />
            <TrustChip icon={<IconCheck size={12} color={BRAND.accent} />} text="Ohne Anmeldung starten" />
          </div>
        </div>

        {/* RIGHT: Magnet-Motion — Ernährung + Fitness ziehen sich an → Athlyn entsteht */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <HeroMagnet />
        </div>
      </div>
    </section>
  );
}

// ─── HERO MAGNET MOTION ──────────────────────────────────
// Zwei Magnete — Ernährung (links, warm) + Fitness (rechts, dunkel) — werden
// mit beschleunigender "Anziehung" aufeinander zugezogen, prallen mit einem
// Lime-Burst zusammen, und daraus entsteht Athlyn (das Phone-Mockup).
// Reine CSS-Keyframes (siehe <style>-Block), respektiert prefers-reduced-motion.
function HeroMagnet() {
  return (
    <div className="magnet-stage">
      {/* Anziehungsfeld zwischen den Polen */}
      <div className="magnet-field" aria-hidden />

      {/* Pol 1 — Ernährung */}
      <div
        className="magnet-puck magnet-puck--nutrition"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(54,30,8,0.15), rgba(54,30,8,0.65)), url('${HERO_IMG_KITCHEN}')` }}
      >
        <span className="magnet-label">Ernährung</span>
      </div>

      {/* Pol 2 — Fitness */}
      <div
        className="magnet-puck magnet-puck--fitness"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(8,12,18,0.15), rgba(8,12,18,0.7)), url('${HERO_IMG_GYM}')` }}
      >
        <span className="magnet-label">Fitness</span>
      </div>

      {/* Kollisions-Burst */}
      <div className="magnet-burst" aria-hidden />

      {/* Athlyn entsteht — Phone startet auf der Startseite, switcht dann ins Dashboard */}
      <div className="magnet-phone">
        <div className="phone-float">
          <PhoneMockup>
            <div className="phone-screens">
              <div className="phone-screen phone-screen--start">
                <PreviewStart />
              </div>
              <div className="phone-screen phone-screen--home">
                <PreviewHome />
              </div>
            </div>
          </PhoneMockup>
        </div>
      </div>
    </div>
  );
}

function TrustChip({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: BRAND.mutedOnDark,
        fontWeight: 500,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: BRAND.accentSoftDark,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </span>
      {text}
    </div>
  );
}

function AppStoreBadge({ location = 'hero' }: { location?: string }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => trackEvent('app_store_click', { location })}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 18px',
        borderRadius: 12,
        background: BRAND.accent,
        color: BRAND.accentText,
        fontWeight: 700,
        fontSize: 14,
        cursor: 'pointer',
        boxShadow: `0 8px 24px ${BRAND.accent}40`,
      }}
      aria-label="App Store — bald verfügbar"
    >
      <span style={{ fontSize: 20 }}></span>
      <div style={{ textAlign: 'left', lineHeight: 1.1 }}>
        <div style={{ fontSize: 10, opacity: 0.7 }}>Bald im</div>
        <div style={{ fontSize: 15, fontWeight: 700 }}>App Store</div>
      </div>
    </div>
  );
}

// ─── PHONE MOCKUP ────────────────────────────────────────

function PhoneMockup({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 300,
        height: 600,
        borderRadius: 44,
        background: '#1A1F26',
        padding: 8,
        boxShadow: `
          0 32px 80px rgba(0,0,0,0.55),
          0 16px 32px rgba(0,0,0,0.35),
          inset 0 0 0 1.5px #2C3340
        `,
        position: 'relative',
      }}
    >
      {/* Notch */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 100,
          height: 26,
          background: '#0B0D11',
          borderRadius: '0 0 16px 16px',
          zIndex: 2,
        }}
      />
      {/* Screen */}
      <div
        style={{
          width: '100%',
          height: '100%',
          background: BRAND.bg,
          borderRadius: 36,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── PREVIEW: START (Splash) ─────────────────────────────
// Athlyn-Startseite, die der Phone-Switch zuerst zeigt, bevor er ins Dashboard
// (PreviewHome) wechselt.
function PreviewStart() {
  return (
    <div
      style={{
        height: '100%',
        background: `radial-gradient(120% 80% at 50% 30%, ${BRAND.bgAlt} 0%, ${BRAND.bg} 70%)`,
        color: BRAND.textOnDark,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div
        className="phone-start-logo"
        style={{
          filter: `drop-shadow(0 12px 36px ${BRAND.accent}55)`,
        }}
      >
        <AthlynLogo size={76} color={BRAND.textOnDark} />
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1.5 }}>
        Athl<span style={{ color: BRAND.accent }}>yn</span>
      </div>
      <div style={{ fontSize: 13, color: BRAND.mutedOnDark, maxWidth: 200, lineHeight: 1.4 }}>
        Disziplin schlägt Perfektion.
      </div>
    </div>
  );
}

// ─── PREVIEW: HOME ───────────────────────────────────────

function PreviewHome() {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      style={{
        padding: '40px 18px 18px',
        height: '100%',
        background: BRAND.bg,
        color: BRAND.textOnDark,
        position: 'relative',
      }}
    >
      {/* Greeting */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: BRAND.mutedOnDark, fontWeight: 600 }}>
          DIENSTAG · 19. MAI
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>
          Guten Morgen
        </div>
      </div>

      {/* Big ring */}
      <div
        style={{
          background: BRAND.bgAlt,
          borderRadius: 18,
          padding: 16,
          marginBottom: 12,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 120,
            height: 120,
            margin: '0 auto',
          }}
        >
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="48"
              fill="none"
              stroke={BRAND.borderDark}
              strokeWidth="10"
            />
            <circle
              cx="60"
              cy="60"
              r="48"
              fill="none"
              stroke={BRAND.accent}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray="301.6"
              strokeDashoffset={animate ? 96 : 301.6}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(0.16,1,0.3,1)' }}
            />
          </svg>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800 }}>1.847</div>
            <div style={{ fontSize: 10, color: BRAND.mutedOnDark }}>
              / 2.640 kcal
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: 10,
            fontSize: 11,
          }}
        >
          <MacroPill label="P" value="142g" pct={68} />
          <MacroPill label="C" value="186g" pct={62} />
          <MacroPill label="F" value="62g" pct={75} />
        </div>
      </div>

      {/* Hydration strip */}
      <div
        style={{
          background: BRAND.bgAlt,
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <IconDroplet size={20} color="#3B82F6" />
        <div style={{ flex: 1 }}>
          <div
            style={{
              height: 6,
              background: BRAND.borderDark,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: animate ? '64%' : '0%',
                background: '#3B82F6',
                borderRadius: 3,
                transition: 'width 1.8s cubic-bezier(0.16,1,0.3,1) 0.4s',
              }}
            />
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700 }}>1.6 / 2.5 L</div>
      </div>

      {/* Mini meal row */}
      <div
        style={{
          background: BRAND.bgAlt,
          borderRadius: 14,
          padding: '10px 12px',
          fontSize: 11,
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: BRAND.mutedOnDark,
            fontWeight: 600,
            marginBottom: 6,
            letterSpacing: 0.6,
          }}
        >
          LETZTE MAHLZEIT
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: BRAND.accentSoftDark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconMeal size={14} color={BRAND.accent} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Lachs + Reis</div>
            <div style={{ fontSize: 10, color: BRAND.mutedOnDark }}>
              13:20 · 612 kcal · 48g P
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MacroPill({
  label,
  value,
  pct,
}: {
  label: string;
  value: string;
  pct: number;
}) {
  return (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <div style={{ color: BRAND.mutedOnDark, fontSize: 9 }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 11 }}>{value}</div>
      <div
        style={{
          height: 3,
          background: BRAND.borderDark,
          borderRadius: 2,
          marginTop: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: BRAND.accent,
          }}
        />
      </div>
    </div>
  );
}

// ─── STATS BAR (count-up) ────────────────────────────────

function StatsBar() {
  const { ref, visible } = useFadeIn<HTMLDivElement>();

  return (
    <section
      ref={ref}
      className={`fade-in ${visible ? 'visible' : ''}`}
      style={{
        padding: '56px 24px',
        background: BRAND.surface,
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      <div
        className="stats-row"
        style={{
          maxWidth: 1040,
          margin: '0 auto',
        }}
      >
        <Stat target={10} suffix="s" label="Pro Mahlzeit" caption="Foto statt Eintippen" trigger={visible} />
        <Stat target={2} suffix="" label="Welten, 1 App" caption="Training + Ernährung" trigger={visible} />
        <Stat target={0} suffix="" label="Werbe-Tracker" caption="Wir verkaufen keine Daten" trigger={visible} />
        <Stat target={1} suffix="" label="KI-Coach" caption="Kennt deine echten Zahlen" trigger={visible} />
      </div>
    </section>
  );
}

function Stat({
  target,
  suffix,
  label,
  caption,
  trigger,
}: {
  target: number;
  suffix: string;
  label: string;
  caption: string;
  trigger: boolean;
}) {
  const v = useCountUp(target, 1200, trigger);
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontSize: 'clamp(34px, 5vw, 44px)',
          fontWeight: 800,
          color: BRAND.accent,
          letterSpacing: -1.5,
          lineHeight: 1,
        }}
      >
        {Math.round(v)}
        <span style={{ fontSize: '0.7em' }}>{suffix}</span>
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          marginTop: 8,
          color: BRAND.text,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 2 }}>
        {caption}
      </div>
    </div>
  );
}

// ─── HOW IT WORKS ────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      n: 1,
      title: 'Foto machen',
      desc: 'Kamera auf, Teller fotografieren. Auch im Restaurant. Kein Wiegen, kein Suchen.',
      visual: <StepVisualPhoto />,
    },
    {
      n: 2,
      title: 'KI rechnet',
      desc: 'In Sekunden hast du Kalorien und Nährwerte. Stimmt was nicht? In zwei Taps korrigiert.',
      visual: <StepVisualAnalyze />,
    },
    {
      n: 3,
      title: 'Fertig',
      desc: 'Dein Tag aktualisiert sich live. Der Coach sagt dir, ob du auf Kurs bist.',
      visual: <StepVisualResult />,
    },
  ];

  return (
    <section
      id="features"
      style={{
        padding: '88px 24px',
        background: BRAND.surfaceTint,
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionEyebrow text="So funktioniert's" />
        <h2
          style={{
            fontSize: 'clamp(30px, 4.5vw, 44px)',
            fontWeight: 800,
            letterSpacing: -1,
            margin: '12px 0 12px',
            maxWidth: 700,
            lineHeight: 1.1,
          }}
        >
          Drei Schritte. <span className="shimmer-text">Unter 10 Sekunden.</span>
        </h2>
        <p
          style={{
            fontSize: 16,
            color: BRAND.muted,
            maxWidth: 540,
            margin: '0 0 48px',
            lineHeight: 1.55,
          }}
        >
          Vom Foto zur fertigen Tagesbilanz. Keine Datenbank, keine
          Portions-Rechnerei.
        </p>

        <div className="feature-grid">
          {steps.map((s) => (
            <StepCard key={s.n} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({
  n,
  title,
  desc,
  visual,
}: {
  n: number;
  title: string;
  desc: string;
  visual: React.ReactNode;
}) {
  const { ref, visible } = useFadeIn<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`fade-in ${visible ? 'visible' : ''} feature-card`}
      style={{
        background: BRAND.surface,
        borderRadius: 20,
        padding: 24,
        border: `1px solid ${BRAND.border}`,
        transitionDelay: `${(n - 1) * 100}ms`,
      }}
    >
      <div
        style={{
          height: 180,
          background: BRAND.surfaceTint2,
          borderRadius: 14,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {visual}
      </div>
      <div
        style={{
          display: 'inline-block',
          fontSize: 11,
          fontWeight: 700,
          color: BRAND.accent,
          background: BRAND.accentSoft,
          padding: '4px 10px',
          borderRadius: 999,
          marginBottom: 10,
          letterSpacing: 1,
        }}
      >
        SCHRITT {n}
      </div>
      <div
        style={{
          fontSize: 19,
          fontWeight: 800,
          letterSpacing: -0.3,
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 14,
          color: BRAND.muted,
          lineHeight: 1.55,
        }}
      >
        {desc}
      </div>
    </div>
  );
}

function StepVisualPhoto() {
  return (
    <div style={{ textAlign: 'center', position: 'relative' }}>
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: BRAND.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          boxShadow: `0 0 0 8px ${BRAND.accentSoft}, 0 0 0 16px ${BRAND.accent}10`,
          animation: 'pulseGlow 2.4s ease-in-out infinite',
        }}
      >
        <IconCamera size={36} color={BRAND.accentText} />
      </div>
      {/* corner frame markers */}
      <CornerFrame />
    </div>
  );
}

function CornerFrame() {
  const cornerStyle = {
    position: 'absolute' as const,
    width: 18,
    height: 18,
    border: `2px solid ${BRAND.accent}`,
  };
  return (
    <>
      <div style={{ ...cornerStyle, top: 12, left: 12, borderRight: 'none', borderBottom: 'none' }} />
      <div style={{ ...cornerStyle, top: 12, right: 12, borderLeft: 'none', borderBottom: 'none' }} />
      <div style={{ ...cornerStyle, bottom: 12, left: 12, borderRight: 'none', borderTop: 'none' }} />
      <div style={{ ...cornerStyle, bottom: 12, right: 12, borderLeft: 'none', borderTop: 'none' }} />
    </>
  );
}

function StepVisualAnalyze() {
  return (
    <div style={{ position: 'relative' }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r="44" fill="none" stroke={BRAND.border} strokeWidth="8" />
        <circle
          cx="55"
          cy="55"
          r="44"
          fill="none"
          stroke={BRAND.accent}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray="276.5"
          strokeDashoffset="80"
          transform="rotate(-90 55 55)"
          style={{ animation: 'ringDraw 2s ease-in-out infinite alternate' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 800, color: BRAND.text }}>612</div>
        <div style={{ fontSize: 9, color: BRAND.muted }}>kcal</div>
      </div>
    </div>
  );
}

function StepVisualResult() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '70%' }}>
      <ResultRow label="Protein" pct={75} />
      <ResultRow label="Kohlenhydrate" pct={62} />
      <ResultRow label="Fett" pct={58} />
    </div>
  );
}

function ResultRow({ label, pct }: { label: string; pct: number }) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 10,
          color: BRAND.muted,
          marginBottom: 3,
        }}
      >
        <span>{label}</span>
        <span style={{ color: BRAND.text, fontWeight: 700 }}>{pct}%</span>
      </div>
      <div
        style={{
          height: 6,
          background: BRAND.border,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: BRAND.accent,
            borderRadius: 3,
            animation: `fillBar 1.4s cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
        />
      </div>
    </div>
  );
}

// ─── COMPARISON — „Was wir anders machen" ────────────────
// Sitzt im AIDA-Interest-Slot direkt nach Stats: amplifiziert das
// Problem („andere Apps machen X falsch") und positioniert die
// Lösung daneben. Dark Section bricht den Light-Run und gibt
// visuell Rhythmus. Sechs Pain-Point-Zeilen, Side-by-Side.

interface ComparisonRow {
  topic: string;
  themPain: string;
  usWin: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    topic: 'Essen tracken',
    themPain: 'Jedes Lebensmittel suchen, Barcode scannen, abwiegen. Im Restaurant? Pech.',
    usWin: 'Teller fotografieren — die KI rechnet Kalorien und Nährwerte aus.',
  },
  {
    topic: 'Dein Coach',
    themPain: 'Allgemeine Tipps, die für jeden gleich sind. Oder gar keine.',
    usWin: 'Ein KI-Coach, der deine echten Zahlen aus der App kennt.',
  },
  {
    topic: 'Schätzungen',
    themPain: 'Schön gerechnet, damit du dich gut fühlst — bringt dich aber nicht weiter.',
    usWin: 'Ehrlich gerechnet, eher vorsichtig. So funktioniert dein Plan wirklich.',
  },
  {
    topic: 'Deine Daten',
    themPain: 'Werbe-Tracker, Cross-App-Verfolgung, Datenverkauf an Dritte.',
    usWin: 'Kein Werbe-Tracking. Wir verkaufen deine Daten nicht.',
  },
];

function Comparison() {
  return (
    <section
      style={{
        padding: '96px 24px',
        background: BRAND.bg,
        color: BRAND.textOnDark,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -100,
          left: '40%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: BRAND.accent,
          opacity: 0.05,
          filter: 'blur(120px)',
        }}
      />
      <div style={{ maxWidth: 1040, margin: '0 auto', position: 'relative' }}>
        <SectionEyebrow text="Was wir anders machen" />
        <h2
          style={{
            fontSize: 'clamp(30px, 4.5vw, 44px)',
            fontWeight: 800,
            letterSpacing: -1,
            margin: '14px 0 16px',
            lineHeight: 1.1,
            maxWidth: 760,
          }}
        >
          Andere Apps versprechen viel.{' '}
          <span style={{ color: BRAND.accent }}>Wir liefern, was zählt.</span>
        </h2>
        <p
          style={{
            fontSize: 16,
            color: BRAND.mutedOnDark,
            maxWidth: 580,
            margin: '0 0 44px',
            lineHeight: 1.55,
          }}
        >
          Was die meisten Fitness-Apps machen — und was Athlyn stattdessen tut.
        </p>

        {/* Header-Row (nur Desktop sichtbar) */}
        <div
          className="cmp-headers"
          style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr 1fr',
            gap: 16,
            padding: '0 16px 12px',
            borderBottom: `1px solid ${BRAND.borderDark}`,
            marginBottom: 8,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: BRAND.mutedOnDark,
          }}
        >
          <div />
          <div>Andere Apps</div>
          <div style={{ color: BRAND.accent }}>Athlyn</div>
        </div>

        {COMPARISON_ROWS.map((row, i) => (
          <ComparisonRowItem key={row.topic} row={row} index={i} />
        ))}
      </div>
    </section>
  );
}

function ComparisonRowItem({ row, index }: { row: ComparisonRow; index: number }) {
  const { ref, visible } = useFadeIn<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`fade-in ${visible ? 'visible' : ''} cmp-row`}
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr 1fr',
        gap: 16,
        padding: '20px 16px',
        borderBottom:
          index === COMPARISON_ROWS.length - 1
            ? 'none'
            : `1px solid ${BRAND.borderDark}`,
        transitionDelay: `${index * 60}ms`,
      }}
    >
      <div
        className="cmp-topic"
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: BRAND.textOnDark,
          alignSelf: 'center',
        }}
      >
        {row.topic}
      </div>

      <div
        className="cmp-cell cmp-them"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          fontSize: 14,
          color: BRAND.mutedOnDark,
          lineHeight: 1.5,
        }}
      >
        <span
          style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            color: BRAND.mutedOnDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 1,
          }}
        >
          <IconX size={11} color={BRAND.mutedOnDark} />
        </span>
        <span>{row.themPain}</span>
      </div>

      <div
        className="cmp-cell cmp-us"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          fontSize: 14,
          color: BRAND.textOnDark,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        <span
          style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: BRAND.accentSoftDark,
            color: BRAND.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 1,
          }}
        >
          <IconCheck size={12} color={BRAND.accent} />
        </span>
        <span>{row.usWin}</span>
      </div>
    </div>
  );
}

// ─── FEATURE: FOTO-TRACKING ──────────────────────────────

function FeatureFotoTracking() {
  return (
    <FeatureSection
      eyebrow="Foto-Tracking"
      title="Mahlzeit fotografieren — den Rest macht die KI."
      desc="Die KI erkennt Gerichte aus aller Welt, schätzt die Portion und rechnet Kalorien und Nährwerte aus. Im Zweifel rechnet sie eher vorsichtig — so bleibt dein Tag auch dann auf Kurs, wenn die Schätzung mal daneben liegt."
      bullets={[
        'Erkennt auch Restaurant-Essen und gemischte Gerichte',
        'Stimmt was nicht? In zwei Taps korrigiert',
        'Dein Foto bleibt auf deinem Gerät gespeichert',
      ]}
      visual={<PreviewMealCard />}
    />
  );
}

function PreviewMealCard() {
  return (
    <div
      style={{
        background: BRAND.surface,
        borderRadius: 22,
        padding: 22,
        boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
        border: `1px solid ${BRAND.border}`,
        maxWidth: 360,
      }}
    >
      {/* faux photo */}
      <div
        style={{
          height: 140,
          borderRadius: 14,
          background: `linear-gradient(135deg, #F4A261 0%, #E76F51 40%, #2A9D8F 100%)`,
          marginBottom: 14,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 50%)`,
          }}
        />
      </div>

      {/* AI badge */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          background: BRAND.accentSoft,
          color: BRAND.accent,
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          marginBottom: 10,
          letterSpacing: 0.5,
        }}
      >
        <IconZap size={10} color={BRAND.accent} /> KI-ANALYSE
      </div>

      <div
        style={{
          fontSize: 17,
          fontWeight: 800,
          color: BRAND.text,
        }}
      >
        Lachs-Bowl mit Reis
      </div>
      <div
        style={{
          fontSize: 12,
          color: BRAND.muted,
          marginBottom: 14,
        }}
      >
        ca. 380g · konservativ geschätzt
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        <MacroTile label="kcal" value="612" color={BRAND.accent} />
        <MacroTile label="P" value="48g" color={BRAND.accent} />
        <MacroTile label="C" value="58g" color="#3B82F6" />
        <MacroTile label="F" value="22g" color={BRAND.warning} />
      </div>
    </div>
  );
}

function MacroTile({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '10px 4px',
        background: BRAND.surfaceTint,
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 9, color: BRAND.muted, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

// ─── FEATURE: COACH CHAT ─────────────────────────────────

function FeatureCoachChat() {
  return (
    <FeatureSection
      reverse
      eyebrow="KI-Coach"
      title="Ein Coach, der deine Zahlen kennt."
      desc="Frag den Coach, was du willst. Er sieht deine Mahlzeiten, dein Gewicht und dein Training der letzten Tage — und gibt dir echte Antworten auf deine Situation, statt allgemeiner Tipps."
      bullets={[
        'Erkennt Stillstand und sagt dir, was du anpassen kannst',
        'Meldet sich von selbst, wenn etwas auffällt — ohne zu nerven',
        'Kein medizinischer Rat — ehrlich und verständlich erklärt',
      ]}
      visual={<PreviewChat />}
    />
  );
}

function PreviewChat() {
  return (
    <div
      style={{
        background: BRAND.surface,
        borderRadius: 22,
        padding: 18,
        boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
        border: `1px solid ${BRAND.border}`,
        maxWidth: 360,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 12,
          borderBottom: `1px solid ${BRAND.border}`,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${BRAND.accent}, ${BRAND.accentDeep})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconZap size={18} color={BRAND.textOnDark} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>Coach</div>
          <div style={{ fontSize: 10, color: BRAND.accent, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: BRAND.accent }} />
            kennt deine Daten
          </div>
        </div>
      </div>

      {/* User bubble */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <div
          style={{
            background: BRAND.accent,
            color: BRAND.accentText,
            padding: '10px 14px',
            borderRadius: '14px 14px 4px 14px',
            fontSize: 13,
            maxWidth: '80%',
          }}
        >
          Warum nehme ich nicht ab?
        </div>
      </div>

      {/* Coach bubble */}
      <div style={{ display: 'flex', marginBottom: 10 }}>
        <div
          style={{
            background: BRAND.surfaceTint,
            color: BRAND.text,
            padding: '12px 14px',
            borderRadius: '14px 14px 14px 4px',
            fontSize: 13,
            maxWidth: '85%',
            lineHeight: 1.5,
          }}
        >
          Deine Aufnahme der letzten 7 Tage liegt im Schnitt{' '}
          <strong style={{ color: BRAND.accent }}>+180 kcal über Ziel</strong>.
          Hauptquelle: Wochenenden (Fr-So +320 kcal). Wenn du das Wochenende
          ausgleichst, sind wir bei -300/Tag.
        </div>
      </div>

      {/* Typing */}
      <div
        className="type-dots"
        style={{
          display: 'inline-flex',
          background: BRAND.surfaceTint,
          padding: '10px 14px',
          borderRadius: '14px 14px 14px 4px',
          alignItems: 'center',
        }}
      >
        <span /><span /><span />
      </div>
    </div>
  );
}

// ─── FEATURE: TRAINING ───────────────────────────────────

function FeatureTraining() {
  return (
    <FeatureSection
      eyebrow="Training"
      title="Plan, Tracking und Pausen-Timer — in einer App."
      desc="Fertige Trainingspläne, die du pro Tag anpassen kannst. Bei jeder Übung siehst du, was du letztes Mal geschafft hast — so steigerst du dich gezielt. Deine Trainingstage wählst du selbst."
      bullets={[
        'Fertige Wochenpläne, frei anpassbar',
        'Zeigt dein letztes Gewicht pro Übung',
        'Pausen-Timer zwischen den Sätzen',
        'An Ruhetagen Tipps zur Erholung statt leerer Bildschirm',
      ]}
      visual={<PreviewTraining />}
    />
  );
}

function PreviewTraining() {
  const days = [
    { label: 'Mo', active: true },
    { label: 'Di', active: false, off: true },
    { label: 'Mi', active: false },
    { label: 'Do', active: false, off: true },
    { label: 'Fr', active: false },
    { label: 'Sa', active: false, off: true },
    { label: 'So', active: false },
  ];

  return (
    <div
      style={{
        background: BRAND.surface,
        borderRadius: 22,
        padding: 20,
        boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
        border: `1px solid ${BRAND.border}`,
        maxWidth: 380,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: BRAND.muted,
          marginBottom: 10,
          letterSpacing: 0.5,
        }}
      >
        WOCHENPLAN
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
        {days.map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: 'center',
              padding: '8px 0',
              borderRadius: 10,
              background: d.active ? BRAND.accentSoft : BRAND.surfaceTint,
              border: d.active ? `1.5px solid ${BRAND.accent}` : '1.5px solid transparent',
              opacity: d.off ? 0.4 : 1,
              fontSize: 11,
              fontWeight: 700,
              color: d.active ? BRAND.accent : BRAND.text,
            }}
          >
            {d.label}
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '6px 10px',
          background: BRAND.accentSoft,
          color: BRAND.accent,
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          display: 'inline-block',
          marginBottom: 8,
        }}
      >
        HEUTE · PUSH
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
        Brust + Schultern
      </div>

      {[
        { name: 'Bankdrücken', sets: '4 × 8', last: '80kg × 8' },
        { name: 'Schulterdrücken', sets: '3 × 10', last: '32kg × 10' },
        { name: 'Dips', sets: '3 × AMRAP', last: '12 Reps' },
      ].map((ex) => (
        <div
          key={ex.name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            background: BRAND.surfaceTint,
            borderRadius: 10,
            marginBottom: 6,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: BRAND.accentSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconDumbbell size={16} color={BRAND.accent} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{ex.name}</div>
            <div style={{ fontSize: 11, color: BRAND.muted }}>{ex.sets} · Letztes: {ex.last}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── FEATURE: GAMIFICATION ───────────────────────────────

function FeatureGamification() {
  return (
    <FeatureSection
      reverse
      eyebrow="Motivation"
      title="Dranbleiben fällt leichter, wenn du Fortschritt siehst."
      desc="Kleine Tagesziele, die sich an dein Training anpassen. Eine Serie zählt, wie viele Tage du am Stück durchhältst — und Erfolge schaltest du frei, sobald du sie verdienst."
      bullets={[
        'Tagesziele, die du abhaken kannst',
        'Serien für Protein, Tracking und Training',
        'Erfolge zum Freischalten',
      ]}
      visual={<PreviewQuests />}
    />
  );
}

function PreviewQuests() {
  const quests = [
    { label: 'Protein-Ziel erreichen', done: true, pct: 100 },
    { label: 'Mindestens 4 Mahlzeiten', done: false, pct: 75 },
    { label: 'Wasser-Ziel erreichen', done: false, pct: 64 },
    { label: 'Heute trainieren', done: true, pct: 100 },
  ];

  return (
    <div
      style={{
        background: BRAND.surface,
        borderRadius: 22,
        padding: 22,
        boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
        border: `1px solid ${BRAND.border}`,
        maxWidth: 380,
      }}
    >
      {/* Streak banner — lime gradient, dark text */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND.accent}, ${BRAND.accentDeep})`,
          color: BRAND.accentText,
          padding: '14px 16px',
          borderRadius: 14,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 28 }}>🔥</div>
        <div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>PROTEIN-STREAK</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>12 Tage in Folge</div>
        </div>
      </div>

      {/* Quests */}
      <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.muted, marginBottom: 10, letterSpacing: 0.5 }}>
        HEUTE · 2 / 4 QUESTS
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {quests.map((q, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              background: q.done ? BRAND.accentSoft : BRAND.surfaceTint,
              borderRadius: 10,
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: q.done ? BRAND.accent : BRAND.surface,
                border: q.done ? 'none' : `1.5px solid ${BRAND.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {q.done && <IconCheck size={12} color={BRAND.accentText} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: q.done ? BRAND.accent : BRAND.text }}>
                {q.label}
              </div>
              <div
                style={{
                  height: 3,
                  background: BRAND.border,
                  borderRadius: 2,
                  marginTop: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${q.pct}%`,
                    background: BRAND.accent,
                    borderRadius: 2,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Badge shelf */}
      <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.muted, marginBottom: 8, letterSpacing: 0.5 }}>
        ERFOLGE
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {['🏆', '🥇', '🔥', '💪', '⚡', '🌟', '🎯'].map((emoji, i) => (
          <div
            key={i}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: i < 4 ? BRAND.accentSoft : BRAND.surfaceTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              opacity: i < 4 ? 1 : 0.35,
            }}
          >
            {emoji}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FEATURE: INSIGHTS ───────────────────────────────────

function FeatureInsights() {
  return (
    <FeatureSection
      eyebrow="Auswertung"
      title="Du siehst, was läuft — und was nicht."
      desc="Athlyn erkennt Trends von selbst: Wenn dein Gewicht steht oder dein Protein zu niedrig ist, sagt die App dir Bescheid. Jeden Sonntag bekommst du deine Wochenbilanz auf einen Blick."
      bullets={[
        'Warnung, wenn dein Gewicht zu lange steht',
        'Hinweis, wenn dein Protein zu niedrig ist',
        'Wochenrückblick im Vergleich zur Vorwoche',
      ]}
      visual={<PreviewInsight />}
    />
  );
}

function PreviewInsight() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        maxWidth: 380,
      }}
    >
      {/* Insight card */}
      <div
        style={{
          background: BRAND.surface,
          borderRadius: 18,
          padding: 18,
          boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
          border: `1px solid ${BRAND.border}`,
          display: 'flex',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconInfo size={20} color={BRAND.warning} />
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: BRAND.warning,
              letterSpacing: 1,
              marginBottom: 2,
            }}
          >
            INSIGHT
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Dein Gewicht steht seit 14 Tagen bei 78 kg
          </div>
          <div style={{ fontSize: 12, color: BRAND.muted, lineHeight: 1.4 }}>
            Du isst konstant — kann an Wasser im Körper liegen. Coach fragen?
          </div>
        </div>
      </div>

      {/* Recap card */}
      <div
        style={{
          background: BRAND.bg,
          color: BRAND.textOnDark,
          borderRadius: 18,
          padding: 18,
          boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: BRAND.mutedOnDark, letterSpacing: 1 }}>
              WOCHEN-RECAP · KW 20
            </div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Solide Woche.</div>
          </div>
          <div
            style={{
              padding: '2px 8px',
              background: BRAND.accentSoftDark,
              color: BRAND.accent,
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            +0.4kg
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <RecapStat label="Ø kcal" value="2.480" />
          <RecapStat label="Ø Protein" value="178g" />
          <RecapStat label="Workouts" value="3/4" />
        </div>
      </div>
    </div>
  );
}

function RecapStat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '8px 10px',
        background: BRAND.bgAlt,
        borderRadius: 10,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 9, color: BRAND.mutedOnDark, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.accent }}>{value}</div>
    </div>
  );
}

// ─── GENERIC FEATURE SECTION ─────────────────────────────

interface FeatureSectionProps {
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  visual: React.ReactNode;
  reverse?: boolean;
}

function FeatureSection({ eyebrow, title, desc, bullets, visual, reverse }: FeatureSectionProps) {
  const { ref, visible } = useFadeIn<HTMLDivElement>();
  return (
    <section
      ref={ref}
      className={`fade-in ${visible ? 'visible' : ''}`}
      style={{
        padding: '80px 24px',
        background: BRAND.surface,
        borderBottom: `1px solid ${BRAND.border}`,
      }}
    >
      <div
        className={`grid-2 ${reverse ? 'zigzag-reverse' : ''}`}
        style={{ maxWidth: 1120, margin: '0 auto' }}
      >
        <div className="zz-text">
          <SectionEyebrow text={eyebrow} />
          <h2
            style={{
              fontSize: 'clamp(28px, 4vw, 38px)',
              fontWeight: 800,
              letterSpacing: -1,
              margin: '14px 0 16px',
              lineHeight: 1.15,
            }}
          >
            {title}
          </h2>
          <p
            style={{
              fontSize: 16,
              color: BRAND.muted,
              lineHeight: 1.6,
              marginBottom: 22,
            }}
          >
            {desc}
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {bullets.map((b) => (
              <li
                key={b}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  fontSize: 14,
                  color: BRAND.textSoft,
                  lineHeight: 1.5,
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: BRAND.accentSoft,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: 2,
                  }}
                >
                  <IconCheck size={11} color={BRAND.accent} />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div
          className="zz-viz"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {visual}
        </div>
      </div>
    </section>
  );
}

// ─── CONSERVATIVE BIAS BLOCK ─────────────────────────────

function BiasBlock() {
  return (
    <section
      style={{
        padding: '88px 24px',
        background: BRAND.bg,
        color: BRAND.textOnDark,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '50%',
          right: '-10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: BRAND.accent,
          opacity: 0.08,
          filter: 'blur(80px)',
        }}
      />
      <div style={{ maxWidth: 880, margin: '0 auto', position: 'relative' }}>
        <SectionEyebrow text="Ehrlich gerechnet" />
        <h2
          style={{
            fontSize: 'clamp(28px, 4.5vw, 40px)',
            fontWeight: 800,
            letterSpacing: -1,
            margin: '14px 0 20px',
            lineHeight: 1.1,
            maxWidth: 700,
          }}
        >
          Wir rechnen <span style={{ color: BRAND.accent }}>ehrlich</span>{' '}
          — nicht schön.
        </h2>
        <p
          style={{
            fontSize: 17,
            color: BRAND.mutedOnDark,
            lineHeight: 1.55,
            marginBottom: 36,
            maxWidth: 640,
          }}
        >
          Fast jede Fitness-App rechnet zu deinen Gunsten: weniger Kalorien,
          mehr Protein — damit du dich gut fühlst. Athlyn macht das Gegenteil.
        </p>

        <div className="feature-grid">
          <BiasTile
            label="Kalorien"
            shift="+10%"
            arrow="↑"
            color={BRAND.warning}
            caption="Eher hoch geschätzt"
          />
          <BiasTile
            label="Protein"
            shift="−10%"
            arrow="↓"
            color={BRAND.accent}
            caption="Eher niedrig geschätzt"
          />
          <BiasTile
            label="Kohlenhydrate"
            shift="+10%"
            arrow="↑"
            color={BRAND.warning}
            caption="Eher hoch geschätzt"
          />
        </div>

        <div
          style={{
            marginTop: 32,
            padding: 20,
            background: BRAND.bgAlt,
            borderRadius: 14,
            borderLeft: `3px solid ${BRAND.accent}`,
            fontSize: 14,
            color: BRAND.mutedOnDark,
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: BRAND.textOnDark }}>Warum?</strong> Wenn die
          KI mal daneben liegt, dann zu deinem Vorteil — nicht gegen dich. So
          machst du echten Fortschritt, statt dich an geschönten Zahlen zu
          orientieren, die nicht aufgehen.
        </div>
      </div>
    </section>
  );
}

function BiasTile({
  label,
  shift,
  arrow,
  color,
  caption,
}: {
  label: string;
  shift: string;
  arrow: string;
  color: string;
  caption: string;
}) {
  return (
    <div
      style={{
        background: BRAND.bgAlt,
        padding: 24,
        borderRadius: 16,
        border: `1px solid ${BRAND.borderDark}`,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 13, color: BRAND.mutedOnDark, fontWeight: 600 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 44,
          fontWeight: 800,
          letterSpacing: -1.5,
          color,
          margin: '6px 0 4px',
        }}
      >
        {arrow} {shift}
      </div>
      <div style={{ fontSize: 12, color: BRAND.mutedOnDark }}>{caption}</div>
    </div>
  );
}

// ─── PRIVACY & TRUST ─────────────────────────────────────

function PrivacyTrust() {
  const items = [
    {
      icon: <IconLotus size={22} color={BRAND.accent} />,
      title: 'Erstmal nur auf deinem Gerät',
      desc: 'Deine Daten liegen zuerst lokal. Erst wenn du dich anmeldest, werden sie sicher in der Cloud synchronisiert — damit du sie auf jedem Gerät hast.',
    },
    {
      icon: <IconCheck size={22} color={BRAND.accent} />,
      title: 'Kein Werbe-Tracking',
      desc: 'Keine Werbe-IDs, keine Cross-App-Verfolgung. Wir verkaufen deine Daten nicht an Dritte.',
    },
    {
      icon: <IconCamera size={22} color={BRAND.accent} />,
      title: 'Transparent bei der KI',
      desc: 'Für die Analyse geht dein Mahlzeiten-Foto an unseren KI-Anbieter. Wir sagen dir ehrlich, was passiert — kein Kleingedrucktes.',
    },
    {
      icon: <IconInfo size={22} color={BRAND.accent} />,
      title: 'Ehrlich gerechnet',
      desc: 'Unsere vorsichtige Rechenweise (eher mehr Kalorien, eher weniger Protein) ist offengelegt. Keine Black Box.',
    },
  ];

  return (
    <section
      style={{
        padding: '88px 24px',
        background: BRAND.surfaceTint,
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <SectionEyebrow text="Privacy & Trust" />
          <h2
            style={{
              fontSize: 'clamp(30px, 4.5vw, 42px)',
              fontWeight: 800,
              letterSpacing: -1,
              margin: '14px auto 16px',
              lineHeight: 1.1,
              maxWidth: 720,
            }}
          >
            Deine Daten gehören dir. <span style={{ color: BRAND.accent }}>Punkt.</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: BRAND.muted,
              maxWidth: 580,
              margin: '0 auto',
              lineHeight: 1.55,
            }}
          >
            Kein Werbe-Tracking, kein Datenverkauf. Und wir sagen dir ehrlich,
            wann etwas dein Gerät verlässt.
          </p>
        </div>

        <div className="feature-grid">
          {items.map((i) => (
            <div
              key={i.title}
              className="feature-card"
              style={{
                background: BRAND.surface,
                padding: 24,
                borderRadius: 16,
                border: `1px solid ${BRAND.border}`,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: BRAND.accentSoft,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                {i.icon}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
                {i.title}
              </div>
              <div style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.55 }}>
                {i.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ─────────────────────────────────────────────────

function FaqSection() {
  const items = [
    {
      q: 'Was kostet Athlyn?',
      a: 'Athlyn ist kostenlos nutzbar — du kannst direkt loslegen, ohne etwas zu bezahlen. Du brauchst keine Kreditkarte und kein Konto, um zu starten.',
    },
    {
      q: 'Was ist Body Recomposition?',
      a: 'Gleichzeitig Muskeln aufbauen und Fett verlieren. Das gelingt mit genug Protein und gezieltem Training — genau dabei hilft dir Athlyn. Weil die App eher vorsichtig rechnet, landest du nicht versehentlich in einer Aufbau- statt Abnehmphase.',
    },
    {
      q: 'Wo liegen meine Daten?',
      a: 'Zuerst lokal auf deinem Gerät. Sobald du dich anmeldest, werden sie für dich in der Cloud synchronisiert, damit du auf jedem Gerät dieselben Daten hast. Für die Foto-Analyse geht dein Mahlzeiten-Foto an unseren KI-Anbieter.',
    },
    {
      q: 'Gibt es die App im App Store?',
      a: 'Sie kommt bald in den App Store. Bis dahin kannst du die Web-Version direkt im Browser testen — sie lässt sich auf dem Home-Bildschirm wie eine echte App ablegen.',
    },
    {
      q: 'Wie genau ist die KI-Schätzung?',
      a: 'Bei normalen Mahlzeiten liegt sie nah dran, bei sehr ungewöhnlichen Gerichten weniger. Sie rechnet bewusst eher vorsichtig — und wenn etwas nicht stimmt, korrigierst du es in zwei Taps.',
    },
  ];

  return (
    <section
      style={{
        padding: '88px 24px',
        background: BRAND.surface,
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <SectionEyebrow text="FAQ" />
          <h2
            style={{
              fontSize: 'clamp(28px, 4vw, 38px)',
              fontWeight: 800,
              letterSpacing: -1,
              margin: '14px 0 0',
              lineHeight: 1.15,
            }}
          >
            Häufige Fragen
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((it, i) => (
            <FaqItem key={i} q={it.q} a={it.a} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      style={{
        background: BRAND.surfaceTint,
        border: 'none',
        borderRadius: 14,
        padding: '20px 24px',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        font: 'inherit',
        transition: 'background 200ms ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: BRAND.text }}>{q}</span>
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: open ? BRAND.accent : BRAND.surface,
            color: open ? BRAND.textOnDark : BRAND.text,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 600,
            transition: 'all 200ms ease',
            transform: open ? 'rotate(45deg)' : 'rotate(0)',
            flexShrink: 0,
          }}
        >
          +
        </span>
      </div>
      {open && (
        <div
          style={{
            marginTop: 14,
            fontSize: 14,
            color: BRAND.muted,
            lineHeight: 1.6,
            animation: 'slideUp 300ms ease',
          }}
        >
          {a}
        </div>
      )}
    </button>
  );
}

// ─── FINAL CTA ───────────────────────────────────────────

function FinalCTA() {
  return (
    <section
      className="gradient-bg"
      style={{
        color: BRAND.textOnDark,
        padding: '96px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: BRAND.accent,
          opacity: 0.1,
          filter: 'blur(120px)',
        }}
      />
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <h2
          style={{
            fontSize: 'clamp(36px, 6vw, 56px)',
            fontWeight: 800,
            letterSpacing: -2,
            margin: '0 0 18px',
            lineHeight: 1.05,
          }}
        >
          Bereit, wenn <br />
          <span
            style={{
              background: `linear-gradient(135deg, ${BRAND.accent}, ${BRAND.accentLight})`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            du es bist.
          </span>
        </h2>
        <p
          style={{
            fontSize: 17,
            color: BRAND.mutedOnDark,
            margin: '0 0 36px',
            lineHeight: 1.55,
            maxWidth: 520,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          Kostenlos und ohne Anmeldung. Direkt im Browser starten — ganz in
          deinem Tempo.
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 14,
            marginBottom: 28,
          }}
        >
          <AppStoreBadge location="final_cta" />
          <a
            href={APP_URL}
            onClick={() => trackEvent('app_open', { location: 'final_cta' })}
            style={{
              padding: '16px 28px',
              borderRadius: 14,
              background: 'transparent',
              border: `1.5px solid ${BRAND.borderDark}`,
              color: BRAND.mutedOnDark,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            Im Browser testen →
          </a>
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            color: BRAND.mutedOnDark,
          }}
        >
          <IconClock size={14} color={BRAND.mutedOnDark} />
          Onboarding in unter 60 Sekunden
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ──────────────────────────────────────────────

function Footer() {
  return (
    <footer
      style={{
        background: BRAND.bg,
        color: BRAND.mutedOnDark,
        padding: '40px 24px 32px',
        borderTop: `1px solid ${BRAND.borderDark}`,
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Brand white />
        <div
          style={{
            display: 'flex',
            gap: 24,
            fontSize: 13,
            flexWrap: 'wrap',
          }}
        >
          <a href={APP_URL} className="nav-link" style={{ color: BRAND.mutedOnDark, textDecoration: 'none' }}>
            App
          </a>
          <a href="/impressum" className="nav-link" style={{ color: BRAND.mutedOnDark, textDecoration: 'none' }}>
            Impressum
          </a>
          <a href="/datenschutz" className="nav-link" style={{ color: BRAND.mutedOnDark, textDecoration: 'none' }}>
            Datenschutz
          </a>
          <a href="/agb" className="nav-link" style={{ color: BRAND.mutedOnDark, textDecoration: 'none' }}>
            AGB
          </a>
          <a
            href="https://github.com/Nabil91-alt/coachai-vercel"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
            style={{ color: BRAND.mutedOnDark, textDecoration: 'none' }}
          >
            GitHub
          </a>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          © 2026 Athlyn · Body Recomp · Made in Deutschland
        </div>
      </div>
    </footer>
  );
}

// ─── SHARED HELPER ───────────────────────────────────────

function SectionEyebrow({ text }: { text: string }) {
  return (
    <div
      style={{
        display: 'inline-block',
        padding: '5px 14px',
        background: BRAND.accentSoft,
        color: BRAND.accent,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
      }}
    >
      {text}
    </div>
  );
}
